import { TranscoderJobViewModel } from '../transcoder-job-view.model';
import { JobReportViewModel } from './transcoding-job-report/job-report.view-model';
import { IMediaTranscoderJobsViewModelApi } from '../transcoder-job.view-model.api';
import { IRouterProxy } from '../job-settings/job-settings.view-model';
import { MediaRepository } from '../../../../domain/media-repository';
import { EReplaceIdStrategy } from '../../../scheduler/library/library.component';
import { ToastrService } from 'ngx-toastr';
import { HistoryTranscoderJobViewModel } from './history-transcoder-job-view.model';
import { EMediaTranscodingItemStatus } from '../../../../domain/media-transcoder/transcoding-item';

export interface ItemStatusFilterOption {
  value: EMediaTranscodingItemStatus | null;
  label: string;
}

/** History jobs only surface terminal / post-run item statuses (Finished and above). */
const HISTORY_ITEM_STATUS_FILTER_MIN = EMediaTranscodingItemStatus.Finished;

export class MediaTranscoderHistoryViewModel {
  selectedJob: TranscoderJobViewModel;
  selectedReport: JobReportViewModel[];

  allJobs: HistoryTranscoderJobViewModel[] = [];
  selectedItemStatusFilter: EMediaTranscodingItemStatus | null = null;
  nameFilter = '';
  readonly statusFilterOptions: ItemStatusFilterOption[];

  constructor(
    private _api: IMediaTranscoderJobsViewModelApi,
    private _routeProxy: IRouterProxy,
    private _mediaRepository: MediaRepository,
    private _toastr: ToastrService,
  ) {
    this.statusFilterOptions = [
      { value: null, label: 'All' },
      ...Object.keys(EMediaTranscodingItemStatus)
        .filter((key) => isNaN(Number(key)))
        .map((key) => ({
          value:
            EMediaTranscodingItemStatus[
              key as keyof typeof EMediaTranscodingItemStatus
            ],
          label: key,
        }))
        .filter((option) => option.value >= HISTORY_ITEM_STATUS_FILTER_MIN),
    ];
  }

  get jobs(): HistoryTranscoderJobViewModel[] {
    let result = this.allJobs;

    if (this.selectedItemStatusFilter !== null) {
      result = result.filter((job) =>
        job.itemStatuses.includes(this.selectedItemStatusFilter),
      );
    }

    const nameQuery = this.nameFilter.trim().toLowerCase();
    if (nameQuery) {
      result = result.filter((job) =>
        job.name.toLowerCase().includes(nameQuery),
      );
    }

    return result;
  }

  setNameFilter(name: string): void {
    this.nameFilter = name;
    this._api.updateDOM();
  }

  setItemStatusFilter(status: EMediaTranscodingItemStatus | null): void {
    this.selectedItemStatusFilter = status;
    this._api.updateDOM();
  }

  async showReport(job: TranscoderJobViewModel) {
    this.selectedJob = job;
    this.selectedReport = null;
    this._api.updateDOM();

    this.selectedReport = await this._api.getReport(job.id);
    this._api.updateDOM();
  }

  public async refreshJobs(): Promise<void> {
    this.allJobs = (await this._api.getJobs()) as HistoryTranscoderJobViewModel[];

    this._api.updateDOM();
  }

  public async uploadToMAM(reportEntry: JobReportViewModel): Promise<void> {
    try {
      const { nameFromUrl, fileExtension } =
        this._getFileNameAndExtensionFromUrl(reportEntry.downloadUrl);

      const now = new Date();
      const timestamp = `${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(
        now.getUTCDate(),
      ).padStart(2, '0')}-${now.getUTCFullYear()}_${String(
        now.getUTCHours(),
      ).padStart(2, '0')}-${String(now.getUTCMinutes()).padStart(2, '0')}-${String(
        now.getUTCSeconds(),
      ).padStart(2, '0')}`;

      const fileName = `${nameFromUrl}_rendered_${timestamp}.${fileExtension}`;

      await this._mediaRepository.contentUpload(
        reportEntry.downloadUrl,
        fileName,
        '',
        null,
        null,
        null,
        {
          DuplicateIdHandlingStrategy: EReplaceIdStrategy.ReplaceFiles,
          ForceBypassTranscoding: true,
        },
      );

      this._toastr.success('Imported to MAM successfully!', 'Success', {});
    } catch (e) {
      this._toastr.error('Error while importing to MAM!', 'Error', {});
    }
  }

  closeReport() {
    this.selectedReport = null;
    this.selectedJob = null;
  }

  redirectToEditMam(mediaId: number) {
    this._routeProxy.navigateTo(`mam/editMedia/${mediaId}`, false);
  }

  private _getFileNameAndExtensionFromUrl(url: string): {
    nameFromUrl: string;
    fileExtension: string;
  } {
    const fileNameWithExt = url.split('/').pop()?.split('?')[0] || '';
    const dotIndex = fileNameWithExt.lastIndexOf('.');

    if (dotIndex === -1) {
      return { nameFromUrl: fileNameWithExt, fileExtension: '' };
    }

    return {
      nameFromUrl: fileNameWithExt.substring(0, dotIndex),
      fileExtension: fileNameWithExt.substring(dotIndex + 1),
    };
  }
}
